import { Box, Button, Card, CardContent, Grid } from "@mui/material";
import { Clear, Delete, Download, Edit, Info, Search, Upload } from "@mui/icons-material";
import { GridActionsCellItem, GridRowId, GridRowModel, GridSortModel, GridSortDirection } from "@mui/x-data-grid";
import { parse } from "date-fns";
import { useEffect, useMemo, useState } from "react";

import AddCircle from "@mui/icons-material/AddCircle";
import AutoComplete from "../../../components/hookForms/autoComplete";
import ConfirmationDialog from "../../../components/confirmationDialog";
import { Container } from "../../../theme/globalStyles";
import DataTable from "../../../components/dataTable";
import DialogDetails from "./modal/dialogDetails";
import DialogEdit from "./modal/dialogEdit";
import DialogLoad from "./modal/dialogLoad";
import DialogNew from "./modal/dialogNew";
import InputMask from "../../../components/hookForms/inputMask";
import InputNumber from "../../../components/hookForms/inputNumber";
import Tooltip from "@mui/material/Tooltip";
import messages from "../../../utils/messages";
import { s3download } from "../../../utils/s3";
import serviceFaturaConcessionaria from "../../../services/serviceFaturaConcessionaria";
import { toast } from "react-toastify";
/* eslint-disable react-hooks/exhaustive-deps */
import { useAuth } from "../../../context/global";
import { useForm } from "react-hook-form";

function FaturaConcessionaria(props: any) {
    const { setTitlePage } = useAuth();

    useEffect(() => {
        setTitlePage("Faturamento - Faturas de Concessionárias");
    }, [setTitlePage]);

    const [data, setData] = useState<any[]>([]);
    const [pageSize, setPageSize] = useState(100);
    const [page, setPage] = useState(0);
    const [filters, setFilters] = useState(null);
    const [rowCount, setRowCount] = useState(0);
    const [isLoading, setLoading] = useState(false);
    const [discardState, setDiscardState] = useState({ data: "", open: false });
    const [openNew, setOpenNew] = useState(false);
    const [openLoad, setOpenLoad] = useState(false);

    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
            field: "",
            sort: "" as GridSortDirection,
        },
    ]);

    const [editState, setEditState] = useState({
        open: false,
        id: "",
        saldoAtual: "",
        energiaInjetada: "",
        consumoKWH: "",
        dataVencimento: new Date(),
        flagDebitoAutomatico: "",
        tarifaKWH: "",
    });

    const [detailsState, setDetailsState] = useState({
        open: false,
        id: "",
        dataLeituraAnterior: "",
        dataLeituraAtual: "",
        dataProximaLeitura: "",
        dataEmissao: "",
        dataVencimento: "",
        dataReferencia: "",
        valorFatura: 0,
    });

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
        // eslint-disable-next-line react-hooks/rules-of-hooks
    } = useForm({
        defaultValues: { numeroInstalacao: "", dataReferencia: "", flagDebitoAutomatico: "", saldoAtual: "" },
    });

    const actionColumns = useMemo(
        () => [
            {
                headerName: "Operações",
                field: "operations",
                type: "actions",
                minWidth: 140,
                flex: 1,
                hide: false,
                getActions: (params: { id: GridRowId; row: GridRowModel }) => [
                    <GridActionsCellItem
                        icon={
                            <Tooltip title="Informações da fatura">
                                <Info />
                            </Tooltip>
                        }
                        label="Informações"
                        onClick={() =>
                            setDetailsState({
                                open: true,
                                id: params.id.toString(),
                                dataLeituraAnterior: params.row.dataLeituraAnterior,
                                dataLeituraAtual: params.row.dataLeituraAtual,
                                dataProximaLeitura: params.row.dataProximaLeitura,
                                dataEmissao: params.row.dataEmissao,
                                dataVencimento: params.row.dataVencimento,
                                dataReferencia: "01/" + params.row.dataReferencia,
                                valorFatura: params.row.valorFatura,
                            })
                        }
                    />,
                    <GridActionsCellItem
                        icon={
                            <Tooltip title="Editar">
                                <Edit />
                            </Tooltip>
                        }
                        label="Editar"
                        onClick={() =>
                            setEditState({
                                open: true,
                                id: params.id.toString(),
                                saldoAtual: params.row.saldoAtual,
                                tarifaKWH: params.row.tarifaKWH,
                                energiaInjetada: params.row.energiaInjetada,
                                consumoKWH: params.row.consumoKWH,
                                dataVencimento: parse(params.row.dataVencimento, "dd/MM/yyyy", new Date()),
                                flagDebitoAutomatico:
                                    String(params.row.flagDebitoAutomatico).toUpperCase() === "SIM" ? "Sim" : "Não",
                            })
                        }
                    />,
                    <GridActionsCellItem
                        icon={
                            <Tooltip title="Download">
                                <Download />
                            </Tooltip>
                        }
                        label="Baixar"
                        onClick={() => handleDownload(params.row.numeroInstalacao, params.id.toString())}
                    />,
                    <GridActionsCellItem
                        icon={
                            <Tooltip title="Descartar">
                                <Delete />
                            </Tooltip>
                        }
                        label="Descartar"
                        onClick={() => setDiscardState({ data: params.id.toString(), open: true })}
                        disabled={params.row.flagExcluidaCalculo === "Sim"}
                    />,
                ],
            },
        ],
        []
    );

    const columns = useMemo(
        () => [
            {
                headerName: "ID",
                field: "faturaConcessionariaID",
                type: "number",
                headerAlign: "left",
                identity: true,
                minWidth: 100,
                flex: 1,
            },
            {
                headerName: "Ciclo",
                field: "cicloFaturamento",
                type: "string",
                minWidth: 80,

                flex: 1,
            },
            {
                headerName: "Instalação",
                field: "numeroInstalacao",
                type: "string",
                minWidth: 180,
                flex: 1,
            },
            {
                headerName: "Valor da tarifa kWh",
                field: "tarifaKWH",
                type: "string",
                minWidth: 200,
                flex: 1,
            },
            {
                headerName: "Energia compensada",
                field: "energiaCompensada",
                type: "number",
                headerAlign: "left",
                minWidth: 170,
                flex: 1,
            },
            {
                headerName: "Consumo kWh",
                field: "consumoKWH",
                type: "number",
                headerAlign: "left",
                minWidth: 130,
                flex: 1,
            },
            {
                headerName: "Saldo kWh",
                field: "saldoAtual",
                type: "number",
                headerAlign: "left",
                minWidth: 200,
                flex: 1,
            },
            {
                headerName: "Valor fatura (R$)",
                field: "valorFatura",
                type: "number",
                headerAlign: "left",
                minWidth: 200,
                flex: 1,
            },
            {
                headerName: "Referência",
                field: "dataReferencia",
                type: "date",
                minWidth: 110,
                flex: 1,
            },
            {
                headerName: "Data de vencimento",
                field: "dataVencimento",
                type: "date",
                minWidth: 180,
                flex: 1,
            },
            {
                headerName: "Débito automático",
                field: "flagDebitoAutomatico",
                type: "string",
                minWidth: 140,
                flex: 1,
            },
            ...actionColumns,
        ],
        [actionColumns]
    );

    const debitoAutoOptions = [
        { label: "Todos", value: "Todos" },
        { label: "Sim", value: "Sim" },
        { label: "Não", value: "Não" },
    ];

    const getAllWithFilters = async (filters: any) => {
        setData([]);
        setLoading(true);

        serviceFaturaConcessionaria
            .getWithFilter({
                ...filters,
                take: pageSize,
                currentPage: page + 1,
                sortExpression: sortModel[0]?.field ?? "",
                sortDirection: sortModel[0]?.sort ?? "",
            })
            .then((result) => {
                setData(result?.data.content);
                setRowCount(result?.data.numberOfElements);
            })
            .catch((err) => {
                toast.error(messages.getWithFilterError, err);
                setData([]);
                setRowCount(0);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(
        () => {
            if (props && props.location && props.location.state) {
                getAllWithFilters(props.location.state);
            } else {
                getAllWithFilters(filters);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [page, pageSize, sortModel]
    );

    const handleDownload = async (numeroInstalacao: string, id: string) => {
        const bucket = process.env.REACT_APP_S3_BUCKET_FATURAS_CONCESSIONARIA || "";

        s3download(bucket, `${numeroInstalacao}/${id.toString()}.pdf`);
    };

    const handleDiscard = async () => {
        serviceFaturaConcessionaria
            .discard(discardState.data)
            .then((result) => {
                if (result) {
                    toast.success("Registro descartado com sucesso!");
                }
            })
            .catch((err) => {
                toast.error("Ocorreu um erro ao descartar o registro!", err);
            })
            .finally(() => {
                setDiscardState({ data: "", open: false });
                getAllWithFilters(filters);
            });
    };

    const handleClickOpenNew = () => {
        setOpenNew(true);
    };

    const handleCloseNew = () => {
        setOpenNew(false);
    };

    const onSubmit = (values: any) => {
        const newFilters = {
            ...values,
            dataReferencia: values.dataReferencia ? "01/" + values?.dataReferencia : "",
            flagDebitoAutomatico: values.flagDebitoAutomatico === "Todos" ? "" : values.flagDebitoAutomatico,
            saldoAtual: values.saldoAtual ? Number(values.saldoAtual.replaceAll(".", "").replaceAll(",", ".")) : 0,
        };

        setFilters(newFilters);
        getAllWithFilters(newFilters);
    };

    useEffect(() => {
        if (props && props.location && props.location.state) {
            reset(props.location.state);
            setFilters(props.location.state);
            getAllWithFilters(props.location.state);
        }
    }, [props]);

    const onClear = () => {
        reset({ numeroInstalacao: "", dataReferencia: "", flagDebitoAutomatico: "", saldoAtual: "" });
        getAllWithFilters(null);
    };

    const handleClickOpenLoad = () => {
        setOpenLoad(true);
    };

    const handleCloseLoad = () => {
        setOpenLoad(false);
    };

    const onSuccessAction = () => {
        getAllWithFilters(filters);
    };

    return (
        <Container>
            <Card>
                <CardContent>
                    <Box sx={{ mb: 2 }}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <Grid container spacing={2} display="flex" alignItems="center">
                                <Grid item xs={12} md={4}>
                                    <InputNumber
                                        name="numeroInstalacao"
                                        label="Instalação"
                                        control={control}
                                        error={errors}
                                        decimalScale={0}
                                        noThousandSeparator
                                        maxLength={15}
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <InputMask
                                        name="dataReferencia"
                                        label="Referência"
                                        control={control}
                                        error={errors}
                                        mask="99/9999"
                                    />
                                </Grid>

                                <Grid item xs={12} md={2}>
                                    <Button
                                        style={{ marginTop: 16, marginBottom: 8 }}
                                        variant="contained"
                                        color="warning"
                                        startIcon={<Search />}
                                        disableElevation
                                        type="submit"
                                        fullWidth
                                        size="medium"
                                        disabled={isLoading}
                                    >
                                        Pesquisar
                                    </Button>
                                </Grid>

                                <Grid item xs={12} md={2}>
                                    <Button
                                        style={{ marginTop: 16, marginBottom: 8 }}
                                        variant="contained"
                                        color="primary"
                                        startIcon={<AddCircle />}
                                        onClick={handleClickOpenNew}
                                        disableElevation
                                        fullWidth
                                        size="medium"
                                    >
                                        Novo
                                    </Button>
                                </Grid>
                            </Grid>

                            <Grid container spacing={2} display="flex" alignItems="center">
                                <Grid item xs={12} md={4}>
                                    <AutoComplete
                                        name="flagDebitoAutomatico"
                                        label="Débito automático"
                                        options={debitoAutoOptions}
                                        control={control}
                                        error={errors}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <InputNumber
                                        name="saldoAtual"
                                        label="Saldo"
                                        control={control}
                                        error={errors}
                                        decimalScale={2}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2} display="flex" alignItems="center">
                                    <Button
                                        style={{ marginTop: 16, marginBottom: 8 }}
                                        variant="outlined"
                                        color="warning"
                                        startIcon={<Clear />}
                                        onClick={() => onClear()}
                                        disableElevation
                                        fullWidth
                                        size="medium"
                                    >
                                        Limpar
                                    </Button>
                                </Grid>

                                <Grid item xs={12} md={2} display="flex" alignItems="center">
                                    <Button
                                        style={{ marginTop: 16, marginBottom: 8 }}
                                        variant="contained"
                                        color="primary"
                                        startIcon={<Upload />}
                                        onClick={handleClickOpenLoad}
                                        disableElevation
                                        fullWidth
                                        size="medium"
                                    >
                                        Carregar
                                    </Button>
                                </Grid>
                            </Grid>
                        </form>
                    </Box>

                    <Box sx={{ height: 450 }}>
                        <DataTable
                            columns={columns}
                            rows={data}
                            rowId={(row: GridRowModel) => row.faturaConcessionariaID}
                            rowCount={rowCount}
                            isLoading={isLoading}
                            onPageChange={(currentPage: number) => setPage(currentPage)}
                            onPageSizeChange={(currentPageSize: number) => setPageSize(currentPageSize)}
                            pageSize={pageSize}
                            onSortModelChange={(model: GridSortModel) => setSortModel(model)}
                        />
                    </Box>

                    <ConfirmationDialog
                        open={discardState?.open}
                        title={"Descartar"}
                        message={"Deseja realmente descartar o registro?"}
                        cancelButtonText={"Não"}
                        confirmButtonText={"Sim"}
                        onClose={() => setDiscardState({ data: "", open: false })}
                        onCancel={() => setDiscardState({ data: "", open: false })}
                        onConfirm={() => {
                            setDiscardState({ data: "", open: false });
                            handleDiscard();
                        }}
                    />

                    <DialogNew
                        open={openNew}
                        onSuccess={() => onSuccessAction()}
                        onClose={() => {
                            handleCloseNew();
                        }}
                    />

                    <DialogEdit
                        open={editState.open}
                        onSuccess={() => onSuccessAction()}
                        data={{
                            open: editState.open,
                            id: editState.id,
                            saldoAtual: editState.saldoAtual,
                            energiaInjetada: editState.energiaInjetada,
                            consumoKWH: editState.consumoKWH,
                            dataVencimento: editState.dataVencimento,
                            flagDebitoAutomatico: editState.flagDebitoAutomatico,
                            tarifaKWH: editState.tarifaKWH,
                        }}
                        onClose={() => {
                            setEditState({
                                open: false,
                                id: "",
                                saldoAtual: "0,00",
                                energiaInjetada: "",
                                consumoKWH: "",
                                dataVencimento: new Date(),
                                flagDebitoAutomatico: "",
                                tarifaKWH: "",
                            });
                        }}
                    />

                    <DialogDetails
                        open={detailsState.open}
                        data={{
                            open: detailsState.open,
                            id: detailsState.id,
                            dataLeituraAnterior: detailsState.dataLeituraAnterior,
                            dataLeituraAtual: detailsState.dataLeituraAtual,
                            dataProximaLeitura: detailsState.dataProximaLeitura,
                            dataEmissao: detailsState.dataEmissao,
                            dataVencimento: detailsState.dataVencimento,
                            dataReferencia: detailsState.dataReferencia,
                            valorFatura: detailsState.valorFatura,
                        }}
                        onClose={() => {
                            setDetailsState({
                                open: false,
                                id: "",
                                dataLeituraAnterior: "",
                                dataLeituraAtual: "",
                                dataProximaLeitura: "",
                                dataEmissao: "",
                                dataVencimento: "",
                                dataReferencia: "",
                                valorFatura: 0,
                            });
                        }}
                    />

                    <DialogLoad open={openLoad} onSuccess={() => handleCloseLoad()} onClose={() => handleCloseLoad()} />
                </CardContent>
            </Card>
        </Container>
    );
}

export default FaturaConcessionaria;
